import { fetchJson, postJson } from "@/api/client";
import type { PortalAdapter } from "@/api/portal-adapter";
import type { ClientEvent, EventBatch } from "@/lib/tracker";
import type {
  ClassReportResponse,
  DistrictReportResponse,
  PortalHomeResponse,
  PortalNavigationResponse,
  PortalPopulationReferenceResponse,
  PortalSessionResponse,
  SchoolReportResponse,
  StudentReportResponse,
} from "@/contracts/portal";
import {
  validateClassReportResponse,
  validateDistrictReportResponse,
  validateNavigationResponse,
  validatePopulationReferenceResponse,
  validatePortalHomeResponse,
  validateSchoolReportResponse,
  validateSessionResponse,
  validateStudentReportResponse,
} from "@/api/validate";

export function createHttpPortalAdapter(apiBaseUrl: string): PortalAdapter {
  return {
    postEvents(events: ClientEvent[], init?: RequestInit) {
      const payload: EventBatch = { events };
      return postJson(`${apiBaseUrl}/events`, payload, init);
    },

    async getSession() {
      return validateSessionResponse(await fetchJson<PortalSessionResponse>(`${apiBaseUrl}/me`));
    },

    async getNavigation() {
      return validateNavigationResponse(await fetchJson<PortalNavigationResponse>(`${apiBaseUrl}/navigation`));
    },

    async getPortalHome() {
      return validatePortalHomeResponse(await fetchJson<PortalHomeResponse>(`${apiBaseUrl}/home`));
    },

    async getPopulationReference(): Promise<PortalPopulationReferenceResponse | null> {
      try {
        return validatePopulationReferenceResponse(
          await fetchJson<PortalPopulationReferenceResponse>(`${apiBaseUrl}/population-reference`),
        );
      } catch {
        return null;
      }
    },

    async getDistrictReport(districtId: string) {
      return validateDistrictReportResponse(
        await fetchJson<DistrictReportResponse | null>(`${apiBaseUrl}/districts/${districtId}`),
      );
    },

    async getSchoolReport(districtId: string, schoolId: string) {
      return validateSchoolReportResponse(
        await fetchJson<SchoolReportResponse | null>(
          `${apiBaseUrl}/districts/${districtId}/schools/${schoolId}`,
        ),
      );
    },

    async getClassReport(districtId: string, schoolId: string, classId: string) {
      return validateClassReportResponse(
        await fetchJson<ClassReportResponse | null>(
          `${apiBaseUrl}/districts/${districtId}/schools/${schoolId}/classes/${classId}`,
        ),
      );
    },

    async getStudentReport(districtId: string, schoolId: string, classId: string, studentId: string) {
      return validateStudentReportResponse(
        await fetchJson<StudentReportResponse | null>(
          `${apiBaseUrl}/districts/${districtId}/schools/${schoolId}/classes/${classId}/students/${studentId}/report`,
        ),
      );
    },
  };
}
